(ns minorodata.components.pipeline-dag.menu
  "One canvas-level right-click menu for a pipeline DAG, shared by the global
   pipelines page and the pipeline editor.

   Two things are shared here:

   1. The MECHANISM — `graph-context-menu` is a single controlled Radix menu
      anchored at the cursor, opened from React Flow's `on-node-context-menu`.
      A page that mounts a Radix ContextMenu inside every node pays for N menu
      trees and N permission subscriptions; this pays for one.

   2. The ITEM VOCABULARY — the builders below fix the icon, wording, ordering and
      permission gate of each action, so an item that exists on both surfaces reads
      identically on both.

   What is NOT shared is which items a page shows and what they dispatch: each page
   passes an `:items` fn of `[node perms]`. The global view is a monitoring surface
   (timeline, logs, cross-link to the editor); the editor is an authoring surface
   (delete, reset unsaved changes). Keeping the wiring at the call site is what lets
   the two diverge on purpose while the overlap stays byte-identical."
  (:require
   ["lucide-react" :refer [ChartNoAxesGanttIcon ChevronRight GitGraphIcon Layers
                           PlayCircle RotateCcw ScrollText Settings Sparkles
                           TerminalSquare Trash2]]
   [applied-science.js-interop :as j]
   [minorodata.app.handlers :refer [dispatch]]
   [re-posh.core :as re-posh]
   [ui.dropdown-menu :as dropdown-menu]))


(def ^:private icon-size 14)

(defn- labelled
  "Item body: icon + label on one row."
  [icon label]
  [:span {:class "flex items-center gap-2"}
   [:> icon {:size icon-size}] label])

(defn section
  "Section heading inside the menu."
  [label]
  [dropdown-menu/dropdown-menu-label label])


;; =============================================================================
;; Shared item builders
;; =============================================================================

(defn run-item
  "Run submenu — `Only this one` / `Upstream` / `Downstream`. Collapses to a
   disabled row without write permission, so the action stays visible (and its
   absence explainable) rather than silently vanishing.

   `:on-run` is called with `:single` / `:upstream` / `:downstream`."
  [{:keys [can-write? on-run]}]
  (if can-write?
    [dropdown-menu/dropdown-menu-sub
     {:content [:<>
                [dropdown-menu/dropdown-menu-item {:class "gap-2" :on-click #(on-run :single)}
                 "Only this one"]
                [dropdown-menu/dropdown-menu-item {:class "gap-2" :on-click #(on-run :upstream)}
                 "Upstream"]
                [dropdown-menu/dropdown-menu-item {:class "gap-2" :on-click #(on-run :downstream)}
                 "Downstream"]]}
     [:span {:class "flex items-center gap-2 justify-between w-full"}
      (labelled PlayCircle "Run")
      [:> ChevronRight {:size icon-size}]]]
    [dropdown-menu/dropdown-menu-item {:class "gap-2" :disabled true}
     (labelled PlayCircle "Run")]))

(defn open-tab-item
  "Open the node in a tab. The global page opens an anchored subpipeline tab on the
   page itself; the editor opens one of its own entity tabs."
  [{:keys [label on-click] :or {label "Open in new tab"}}]
  [dropdown-menu/dropdown-menu-item {:class "gap-2" :on-click on-click}
   (labelled Layers label)])

(defn kai-item
  "Ask KAI about the entity, or debug it when its last run failed. Sets the chat
   context + a seeded draft, then opens the KAI modal.

   `sandbox?` names the entity a *sandbox* transform in the draft: the sandbox copy
   and the live one share a name, so an unqualified question reads as being about the
   live transform — and its answer would be about the wrong SQL."
  [{:keys [master-id group-id title failure? sandbox?]}]
  (let [entity (if sandbox? "sandbox transform" "transform")]
    [dropdown-menu/dropdown-menu-item
     {:class    "gap-2"
      :on-click #(do (dispatch [:ai-chat/set-context {:master-id master-id :group-id group-id :name title}])
                     (dispatch [:ai-chat/set-draft
                                (if failure?
                                  (str "Debug why the " entity " \"" title "\" failed?")
                                  (str "Explain the " entity " \"" title "\""))])
                     (dispatch [:modals.events/toggle-modal "ai-modal" true]))}
     (labelled Sparkles (if failure? "Debug with KAI" "Ask KAI"))]))

(defn logs-item
  "Recent logs for the entity. Gated on `:logs/read` by the caller via `:disabled?`."
  [{:keys [disabled? on-click]}]
  [dropdown-menu/dropdown-menu-item
   {:class    "gap-2"
    :disabled disabled?
    :on-click on-click}
   (labelled ScrollText "Logs")])

(defn timeline-item
  "Execution timeline (gantt + executions table) for a transform."
  [{:keys [on-click]}]
  [dropdown-menu/dropdown-menu-item {:class "gap-2" :on-click on-click}
   (labelled ChartNoAxesGanttIcon "Execution timeline")])

(defn settings-item
  "Jump to the entity's settings/details page. `:disabled?` keeps the row visible when
   the page can't be addressed yet (e.g. an extract whose id hasn't resolved)."
  [{:keys [disabled? on-click]}]
  [dropdown-menu/dropdown-menu-item {:class    "gap-2"
                                     :disabled disabled?
                                     :on-click on-click}
   (labelled Settings "Settings")])

(defn sql-console-item
  "Open the transform in the SQL console modal. Write-gated: the console edits."
  [{:keys [transform-id can-write?]}]
  [dropdown-menu/dropdown-menu-item
   {:class    "gap-2"
    :disabled (not can-write?)
    :on-click #(dispatch [:sql-console-modal.events/open-transform transform-id])}
   (labelled TerminalSquare "SQL Console")])

(defn pipeline-editor-item
  "Hand off to the pipeline editor — the write surface for this entity."
  [{:keys [id group-id type name]}]
  [dropdown-menu/dropdown-menu-item
   {:class    "gap-2"
    :on-click #(dispatch [:pipeline-editor.events/navigate-and-open-tab
                          {:id id :group-id group-id :type type :name name}])}
   (labelled GitGraphIcon "Pipeline editor")])

(defn reset-changes-item
  "Discard a sandbox transform's unsaved local command."
  [{:keys [on-click]}]
  [dropdown-menu/dropdown-menu-item
   {:class    "gap-2 text-kleene-text-warning"
    :on-click on-click}
   (labelled RotateCcw "Reset changes")])

(defn delete-item
  "Delete a sandbox transform."
  [{:keys [on-click]}]
  [dropdown-menu/dropdown-menu-item
   {:class    "gap-2 text-kleene-text-error"
    :on-click on-click}
   (labelled Trash2 "Delete")])


;; =============================================================================
;; The menu shell
;; =============================================================================

(defn graph-context-menu
  "One controlled Radix menu for a whole canvas. `:target` is `{:node :x :y}` set by
   the canvas's `on-node-context-menu` (nil when closed); a 0×0 fixed trigger anchors
   the menu at the cursor.

   Permissions are resolved once here — not per node — and handed to `:items`, a fn
   of `[node perms]` returning the vector of items to splice into the content.
   `perms` carries `:can-write?` and `:can-read-logs?`."
  [{:keys [target on-close items]}]
  (let [{:keys [node x y]} target
        can-write?     (or @(re-posh/subscribe [:users.subs/has-permissions? [:connections+transforms/write]])
                           @(re-posh/subscribe [:users.subs/has-permissions? [:transforms/write]]))
        can-read-logs? @(re-posh/subscribe [:users.subs/has-permissions? :logs/read])]
    [dropdown-menu/dropdown-menu-root {:open           (some? target)
                                       :on-open-change #(when-not % (on-close))}
     [dropdown-menu/dropdown-menu-trigger {:as-child true}
      [:span {:aria-hidden true
              :style {:position "fixed" :left (or x 0) :top (or y 0) :width 0 :height 0}}]]
     (when node
       (into [dropdown-menu/dropdown-menu-content
              {:side "bottom" :align "start" :side-offset 0 :hide-arrow true :class "w-68"}]
             (items node {:can-write?     can-write?
                          :can-read-logs? can-read-logs?})))]))

(defn on-context-menu
  "Builds the `:on-node-context-menu` handler for `ui.dag/dag`: prevents the native
   menu and hands `{:node :x :y}` to `set-target!`, which drives `graph-context-menu`.

   `set-target!` is a 1-arg callback rather than an atom so either state style works
   — a reagent atom in a form-2 canvas, or a `useState` setter in an `:f>` one."
  [set-target!]
  (fn [e n]
    (j/call e :preventDefault)
    (set-target! {:node (js->clj (j/get n :data) :keywordize-keys true)
                  :x    (j/get e :clientX)
                  :y    (j/get e :clientY)})))
